import {
  queryOptions,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';
import { useCallback } from 'react';
import { backendErrorsHandler } from '../../utils/Notification/BackendErrorsHandler';
import { getOperationalReturnOrders, getOrderByIdV2, getOrdersHeaderSummaryV2, getOrdersListV2, getRequestedOrdersSummary } from '../../services/order.service';
import { IOrder } from '../../interfaces/Order';
import { IStore } from '../../interfaces/Store';
import OrderStatuses from '../../enum/OrderStatuses';
import {
  CONSOLIDATED_TICKET_NON_CANCELLED_STATUS_FILTER,
  fetchAllStoresConsolidatedTicketOrders
} from '../../utils/consolidatedOrdersTicket';
export const listQueryKey = 'orders';
export const returnOrdersQueryKey = 'order-returns';

interface IQueryFnParams {
  orders: IOrder[];
  totalRows: [{ totalRowsCount: number }];
  amountDueTotal?: number;
}

const getOrderBySequenceQueryOptions = (
  sequence: string,
  storeId: string
) => queryOptions<IOrder | undefined>({
  queryKey: [listQueryKey, 'by-sequence', { sequence, storeId, status: OrderStatuses.COLLECTED }],
  queryFn: async () => {
    try {
      const res = await getOrdersListV2({
        sequence,
        storeId,
        status: OrderStatuses.COLLECTED,
        pageNum: 0,
        limit: 1,
        sequenceSort: '-1',
        includeAmountDueTotal: false,
        fetchSection: 'true'
      });
      if (!res?.data?.data) {
        throw new Error('No se encontró el pedido.');
      }
      return res.data.data.orders?.[0] as IOrder | undefined;
    } catch (error: unknown) {
      const requestError = error as { response?: { data?: { errors?: unknown } } };
      console.log('error caught', requestError.response?.data?.errors);
      throw backendErrorsHandler(requestError);
    }
  }
});

export const useFetchOrderBySequence = () => {
  const queryClient = useQueryClient();

  return useCallback(
    (sequence: string, storeId: string) =>
      queryClient.fetchQuery(getOrderBySequenceQueryOptions(sequence, storeId)),
    [queryClient]
  );
};

export const CONSOLIDATED_ORDERS_PAGE_QUERY_KEY = 'consolidated-orders-page';

export interface IGetOrdersPageQuery {
  customerId: string;
  storeId: string;
  fromDate: string;
  toDate: string;
  pageNum: number;
  limit: number;
  fetchSection: string;
  status: string;
}

export const useGetOrdersPage = (
  enabled: boolean,
  query: IGetOrdersPageQuery
) => useQuery<IQueryFnParams>({
  staleTime: 0,
  enabled,
  queryKey: [
    CONSOLIDATED_ORDERS_PAGE_QUERY_KEY,
    query.customerId,
    query.storeId,
    query.fromDate,
    query.toDate,
    query.pageNum,
    query.limit,
    query.fetchSection,
    query.status
  ],
  queryFn: async ({ signal }) => {
    try {
      const res = await getOrdersListV2({
        ...query,
        sequenceSort: '-1',
        signal
      });

      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data as IQueryFnParams;
    } catch (error: unknown) {
      const requestError = error as {
        name?: string;
        response?: { data?: { errors?: unknown } };
      };
      if (requestError.name !== 'AbortError' && !signal.aborted) {
        backendErrorsHandler(requestError);
        console.log('error caught', requestError.response?.data?.errors);
      }
      throw error;
    }
  }
});

export interface IGetAllStoresConsolidatedOrdersQuery {
  customerId: string;
  stores: IStore[];
  fromDate: string;
  toDate: string;
  limit: number;
  fetchSection: string;
  status: string;
}

export const useGetAllStoresConsolidatedOrders = (
  enabled: boolean,
  query: IGetAllStoresConsolidatedOrdersQuery
) => {
  const storeScopes = query.stores
    .filter((store): store is IStore & { _id: string; timezone: string } =>
      Boolean(store._id && store.timezone)
    )
    .map((store) => ({ _id: store._id, timezone: store.timezone }))
    .sort((left, right) => left._id.localeCompare(right._id));

  return useQuery<IOrder[]>({
    staleTime: 0,
    enabled,
    queryKey: [
      'consolidated-orders-all-stores',
      query.customerId,
      storeScopes.map((store) => `${store._id}:${store.timezone}`).join(','),
      query.fromDate,
      query.toDate,
      query.limit,
      query.fetchSection,
      query.status
    ],
    queryFn: ({ signal }) => fetchAllStoresConsolidatedTicketOrders({
      customerId: query.customerId,
      stores: storeScopes,
      fromDate: query.fromDate,
      toDate: query.toDate,
      limit: query.limit,
      fetchSection: query.fetchSection,
      status: query.status || CONSOLIDATED_TICKET_NON_CANCELLED_STATUS_FILTER
    }, { signal })
  });
};

export const useGetOrders = (enabled: boolean, query: {
  pageNum?: number,
  storeId?: string | undefined,
  storeIds?: string[] | string | undefined,
  status?: string | undefined,
  name?: string | undefined,
  phone?: string | undefined,
  email?: string | undefined,
  fromDate?: string | undefined,
  toDate?: string | undefined,
  sequence?: string | undefined,
  limit: number | undefined,
  sequenceSort?: string,
  cleanedDateTime?: string | undefined,
  readyDateTime?: string | undefined,
  collectedDateTime?: string | undefined,
  q?: string | undefined,
  pickupFromDate?: string | undefined,
  pickupToDate?: string | undefined,
  deliveryFromDate?: string | undefined,
  deliveryToDate?: string | undefined,
  fetchSection?: string | undefined,
  cfdiId?: string | undefined,
  customerId?: string | undefined,
  assignedEmployee?: string | undefined,
  includeAmountDueTotal?: boolean
}) => useInfiniteQuery<IQueryFnParams>({
  staleTime: 0,
  enabled: enabled,
  queryKey: [
    listQueryKey,
    query.pageNum,
    query.storeId,
    Array.isArray(query.storeIds) ? query.storeIds.join(',') : query.storeIds,
    query.status,
    query.name,
    query.phone,
    query.email,
    query.fromDate,
    query.toDate,
    query.sequence,
    query.limit,
    query.sequenceSort,
    query.cleanedDateTime,
    query.readyDateTime,
    query.collectedDateTime,
    query.q,
    query.pickupFromDate,
    query.pickupToDate,
    query.deliveryFromDate,
    query.deliveryToDate,
    query.fetchSection,
    query.cfdiId,
    query.customerId,
    query.assignedEmployee,
    query.includeAmountDueTotal ?? false
  ],
  initialPageParam: 0,
  getNextPageParam: (lastPage, allPages, lastPageParam) => {
    const currentRows = allPages.reduce((prev, curr) => prev + curr.orders.length, 0) || 0;
    const lastPageValue = (lastPage.totalRows && lastPage.totalRows[0]) || { totalRowsCount: 0 };
    if (lastPageValue.totalRowsCount <= currentRows) {
      return undefined
    }
    return (lastPageParam as number) + 1;
  },
  getPreviousPageParam: (_firstPage, _allPages, firstPageParam) => {
    if ((firstPageParam as number) <= 1) {
      return undefined
    }
    return (firstPageParam as number) - 1
  },
  queryFn: async ({
    queryKey,
    pageParam,
  }: {
    queryKey: readonly unknown[];
    pageParam: unknown;
  }) => {
    try {
      const [_key] = queryKey;
      const nextPageParam = typeof pageParam === 'number' ? pageParam : 0;
      const res = await getOrdersListV2({
        ...query,
        pageNum: query.pageNum || nextPageParam,
        limit: query.limit,
        sequenceSort: query.sequenceSort || '',
        includeAmountDueTotal: query.includeAmountDueTotal ?? false
      });

      if (!res?.data?.data) {
        throw new Error('Oh no! Something went wrong while fetching.');
      }
      return res.data.data as IQueryFnParams;
    } catch (e: any) {
      backendErrorsHandler(e);
      console.log('error caught', e['response']?.data?.['errors']);
      throw e;
    }
  }
});

export const useGetOrdersHeaderSummary = (query: {
  store?: string,
  status?: string
}) => useQuery<{
  amountPaid: number,
  ordersTotal: number
  prepaidAmount: number;
  total: number;
  totalPieces: number;
  totalQuantity: number;
  unpaidTotal: number;
}>({
  enabled: !!query.store,
  queryKey: [
    listQueryKey,
    query.status,
    query.store,
  ],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getOrdersHeaderSummaryV2(query);
      if (!res?.data?.data) {
        return {
          amountPaid: 0,
          ordersTotal: 0,
          prepaidAmount: 0,
          total: 0,
          totalPieces: 0,
          totalQuantity: 0,
          unpaidTotal: 0,
        };
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});


// export const getCustomerHighlightsById = (id: string) => useQuery<any>({
//   queryKey: [listQueryKey, { _id: id }],
//   enabled: !!id,
//   queryFn: async ({ queryKey }) => {
//     try {
//       const [_key, { _id: _id }] = queryKey;
//       const res = await getCustomerHighlightsByIdV2(_id);
//       if (!res?.data?.data) {
//         throw new Error('Oh no! Something went wrong while fetching.');
//       }
//       return res.data.data;
//     } catch (e: any) {
//       console.log('error caught', e['response']?.data?.['errors']);
//       backendErrorsHandler(e);
//       throw e;
//     }
//   }
// });


export const useGetRequestedOrdersSummary = (storeIds: string) => useQuery<{
  count: number;
  storeId: string;
  storeName: string;
}[]>({
  staleTime: 15000, // 15 seconds
  refetchOnWindowFocus: false,
  refetchOnReconnect: false,
  refetchOnMount: false,
  enabled: !!storeIds,
  queryKey: ['requestedOrdersSummary', storeIds],
  queryFn: async ({ queryKey }) => {
    try {
      const [_key] = queryKey;
      const res = await getRequestedOrdersSummary(storeIds);
      if (!res?.data?.data) {
        return {
          amountPaid: 0,
          ordersTotal: 0,
          prepaidAmount: 0,
          total: 0,
          totalPieces: 0,
          totalQuantity: 0,
          unpaidTotal: 0,
        };
      }
      return res.data.data;
    } catch (e: any) {
      console.log('error caught', e['response']?.data?.['errors']);
      backendErrorsHandler(e);
      throw e;
    }
  }
});


export const useGetOrderById = (
  orderId: string,
  params?: { fetchSection?: string },
  options?: { enabled?: boolean; retry?: boolean | number }
) => {
  return useQuery<IOrder>({
    queryKey: [
      listQueryKey,
      { _id: orderId, fetchSection: params?.fetchSection ?? null }
    ],
    enabled: !!orderId?.trim() && (options?.enabled ?? true),
    retry: options?.retry ?? true,
    queryFn: async () => {
      try {
        const res = await getOrderByIdV2(orderId, params);
        if (!res?.data?.data) {
          throw new Error('No se encontró el pedido.');
        }
        return res.data.data;
      } catch (e: any) {
        console.log('error caught', e?.response?.data?.errors);
        throw backendErrorsHandler(e);
      }
    }
  });
};

export const useGetOperationalReturnOrders = (
  sourceOrderId: string,
  options?: { enabled?: boolean }
) => useQuery<IOrder[]>({
  queryKey: [returnOrdersQueryKey, sourceOrderId],
  enabled: !!sourceOrderId?.trim() && (options?.enabled ?? true),
  queryFn: async () => {
    try {
      const res = await getOperationalReturnOrders(sourceOrderId);
      return (res?.data?.data ?? []) as IOrder[];
    } catch (e: any) {
      throw backendErrorsHandler(e);
    }
  }
});
